"use client";

import React, { createContext, useState, useContext, ReactNode } from 'react';
import Snackbar from './Snackbar';

// Define a new type for the possible positions
type SnackbarPosition = 'top' | 'bottom';

// Update the state interface to include the position
interface SnackbarState {
  open: boolean;
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
  position: SnackbarPosition; // Add the position property
}

// Update the context type for the showSnackbar function
interface SnackbarContextType {
  showSnackbar: (
    message: string,
    variant: SnackbarState['variant'],
    position?: SnackbarPosition // Make position optional
  ) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: '',
    variant: 'info',
    position: 'bottom', // Set a default position
  });

  const showSnackbar = (
    message: string,
    variant: SnackbarState['variant'],
    position: SnackbarPosition = 'bottom' // Use a default value
  ) => {
    setSnackbarState({ open: true, message, variant, position });
  };

  const handleClose = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  const value = { showSnackbar };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {snackbarState.open && (
        <Snackbar
          message={snackbarState.message}
          variant={snackbarState.variant}
          onClose={handleClose}
          position={snackbarState.position} // Pass the position to the Snackbar component
        />
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
